import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { ConsentStatus, ConsentType } from '@capacitor-firebase/analytics/dist/esm/definitions';
import { Injectable } from '@angular/core';

import { AnalyticsEvent } from '../../../../Domain/Model/analytics.event';
import { EventRepository } from '../../../../Domain/Port/event-repository';
import { FirebaseErrorLogger } from '../../../../Domain/Port/firebase-error.logger';

/**
 * FirebaseCapacitorEventRepository is a service that extends the EventRepository
 * and provides functionality for logging events using Firebase Analytics
 * within a Capacitor application.
 *
 * It includes error handling by logging any issues encountered during event logging
 * to a FirebaseErrorLogger.
 */
@Injectable({
    providedIn: 'root'
})
export class FirebaseCapacitorEventRepository extends EventRepository {
    private readonly firebaseAnalytics = FirebaseAnalytics;
    private readonly consentEvents: ConsentType[] = [
        ConsentType.AnalyticsStorage,
        ConsentType.FunctionalityStorage,
        ConsentType.AdUserData,
        ConsentType.AdPersonalization,
        ConsentType.PersonalizationStorage,
        ConsentType.AdStorage
    ];

    constructor(private readonly firebaseErrorLogger: FirebaseErrorLogger) {
        super();
    }

    async setAnalyticsConsent(isGiven: boolean): Promise<void> {
        await this.firebaseAnalytics.setEnabled({ enabled: isGiven });

        const status: ConsentStatus = isGiven ? ConsentStatus.Granted : ConsentStatus.Denied;
        await Promise.all(
            this.consentEvents.map(async consentType =>
                this.firebaseAnalytics.setConsent({
                    type: consentType,
                    status
                })
            )
        );
    }

    override async logEvent(event: AnalyticsEvent): Promise<void> {
        try {
            await this.firebaseAnalytics.logEvent({ name: event.name, params: event.params });
        } catch (error) {
            await this.firebaseErrorLogger.logError(event, error);
        }
    }
}
